'use client'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Form } from '@/components/ui/form'
import { FormTextarea } from '@/components/ui/form-textarea'
import { FormInput } from '@/components/ui/input'
import { useCreateSupportMessage } from '@/features/support'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

const contactFormSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
  subject: z.string().min(1, { message: 'Subject is required' }),
  message: z.string().min(1, { message: 'Message is required' }),
})

type ContactFormData = z.infer<typeof contactFormSchema>

interface ContactUsModalProps {
  children: React.ReactNode
}

export function ContactUsModal({ children }: ContactUsModalProps) {
  const [open, setOpen] = useState(false)

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      email: '',
      subject: '',
      message: '',
    },
  })

  const createSupportMessage = useCreateSupportMessage()

  const handleSubmit = async (data: ContactFormData) => {
    try {
      await createSupportMessage.mutateAsync({
        subject: data.subject.trim(),
        message: data.message.trim(),
        userId: 'contact-us-guest', // Special ID for contact us messages
        userEmail: data.email.trim(),
        userName: data.email.trim(), // Use email as name for guest users
        userAvatar: '',
        companyId: '',
        companyName: '',
        priority: 'medium',
        source: 'contact-us', // Add source field to identify contact us messages
      })

      form.reset()
      setOpen(false)
      toast.success("Message sent successfully! We'll get back to you as soon as possible.")
    } catch (error) {
      console.error('Error submitting contact form:', error)
      toast.error('Failed to send message. Please try again.')
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className='sm:max-w-[600px] max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle className='text-2xl font-bold'>Let's Talk</DialogTitle>
          <DialogDescription className='text-base'>
            Have questions or want to learn more? Our team is here to help. Send us a message and we'll get back to you
            as soon as possible.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className='space-y-6 mt-6'>
            <FormInput
              control={form.control}
              name='email'
              label='Email'
              requiredIndicator='*'
              placeholder='Type your email'
            />

            <FormInput
              control={form.control}
              name='subject'
              label='Subject'
              requiredIndicator='*'
              placeholder='Brief description of your issue'
            />

            <FormTextarea
              control={form.control}
              name='message'
              label='Message *'
              placeholder='Please share the details of your request or question'
              rows={6}
            />

            <Button
              type='submit'
              disabled={createSupportMessage.isPending}
              className='w-full bg-neutral-800 text-white hover:bg-neutral-700'
            >
              {createSupportMessage.isPending ? (
                <>
                  <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  Sending...
                </>
              ) : (
                'Get in Touch'
              )}
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
